// Origination's request bodies, ported from CGS2022 originationInsights.vue (the six cards),
// insightsCardExpanded.vue (each card's queryChanged) and originationInsightsChart.vue (the
// four chart series). Kept verbatim, key order included, so the API receives exactly what CGS
// sends. Shared by OriginationInsight.vue and OriginationChart.vue only.

import { dateFns } from '@/helpers/insights/insightDates.js'

// The six cards, in CGS's order: card key, title, and the date field its window filters on.
export const ORIGINATION_CARDS = [
  { key: 'applications', title: 'Applications', field: 'F3142' },
  { key: 'rateLocks', title: 'Rate Locks', field: 'F2149' },
  { key: 'submittedToUW', title: 'Submitted to UW', field: 'LogMSDateSubmittal' },
  { key: 'preQual', title: 'PreQual', field: 'PreQual' },
  { key: 'creditPulled', title: 'Credit Pulled', field: 'CreditReportOrderDate' },
  { key: 'funding', title: 'Funded', field: 'MS_FUN' }
]

function azureId() {
  try { return localStorage.getItem('azure_id') } catch (e) { return null }
}

function dateFields(field, start, end) {
  return [
    { name: 'start', field, type: 'Date', oper: '>=', val: start },
    { name: 'end', field, type: 'Date', oper: '<=', val: end }
  ]
}

// CGS data(): <card>Query / <card>TrendQuery. Today against yesterday.
export function defaultCardQuery(field, trend = false) {
  const day = trend ? dateFns.getPastDate(1) : new Date().toLocaleDateString()
  return { channel: [], branch: [], state: [], lo: [], bda: [], lien: [], queryFields: dateFields(field, day, day), azureId: azureId() }
}

// insightsCardExpanded.queryChanged(e). CGS fills an empty object in this order, so the keys
// arrive in this order too. No locks / warehouseLine / purpose: CGS doesn't send them here.
export function cardQueryFromEvent(field, e, trend = false) {
  const start = trend ? e.insightTrendStartDate : e.insightStartDate
  const end = trend ? e.insightTrendEndDate : e.insightEndDate
  return {
    branch: e.branch,
    lo: e.lo.map(item => item.email),
    bda: e.bda.map(item => item.encompassId),
    lien: e.lien.map(item => item.value),
    amortization: e.amortization.map(item => item.value),
    channel: e.channel,
    state: e.state,
    azureId: e.azureId,
    queryFields: dateFields(field, start.toLocaleDateString(), end.toLocaleDateString()),
    division: e.division.map(item => item.value)
  }
}

// insightsCardExpanded.processInsightsUnitsVol(), verbatim in effect: units are a count of
// items per loan type, volume is the sum of +f2, and "Other" is the API's totals less the
// listed types. It returns the rows instead of writing them onto the API object.
export const CARD_ROW_TITLES = [
  { display: 'Conventional', value: 'Conventional' },
  { display: 'FHA', value: 'FHA' },
  { display: 'VA', value: 'VA' },
  { display: 'USDA', value: 'FarmersHomeAdministration' },
  { display: 'HELOC', value: 'HELOC' }
]
const CARD_TITLE_PROP = 'f1172'

export function unitsVolByType(insights) {
  const items = (insights && insights.items) || []
  const other = { units: +insights.units, volume: +insights.volume }
  const rows = {}
  CARD_ROW_TITLES.forEach(title => {
    const units = items.reduce((prev, cur) => (cur[CARD_TITLE_PROP] === title.value ? prev + 1 : prev), 0)
    const volume = items.reduce((prev, cur) => (cur[CARD_TITLE_PROP] === title.value ? prev + +cur.f2 : prev), 0)
    rows[title.display] = { units, volume }
    other.units -= units
    other.volume -= volume
  })
  rows.Other = other
  return rows
}

// The "LO Summary" rows: insightsCardExpanded's creditPullLOSummary reduce, verbatim.
export function loSummaryOf(items) {
  return (items || []).reduce((acc, item) => {
    const existingItem = acc.find(summaryItem => summaryItem.f1407 === item.f1407)
    const volumeValue = parseFloat(item.loanamount) || 0
    if (existingItem) {
      existingItem.units++
      existingItem.loanamount += volumeValue
    } else {
      acc.push({ f1407: item.f1407, f317: item.f317, units: 1, loanamount: volumeValue })
    }
    return acc
  }, [])
}

// ── the chart ───────────────────────────────────────────────────────────────────────────
// originationInsightsChart.vue's chartslist. "Purchased" is a chart option only; it has no card.
export const CHART_METRICS = [
  { name: 'Applications', field: 'F3142' },
  { name: 'Rate Locks', field: 'F2149' },
  { name: 'Submitted To UW', field: 'LogMSDateSubmittal' },
  { name: 'PreQual', field: 'PreQual' },
  { name: 'Credit Pulled', field: 'CreditReportOrderDate' },
  { name: 'Funded', field: 'MS_FUN' },
  { name: 'Purchased', field: 'logmsstatusPostClosing' }
]

// CGS site.js globals, computed once when the page script loads, as CGS computes them. The
// year bounds are Date objects, so they reach the API as ISO strings exactly as CGS's do.
const now = new Date()
const nowToday = now.getDate()
const currentYear = now.getFullYear()
const lastYear = currentYear - 1
const thisYearStart = new Date(currentYear, 0, 1)
const thisYearEnd = new Date(currentYear, 11, 31)
const lastYearStart = new Date(currentYear - 1, 0, 1)
const lastYearEnd = new Date(currentYear - 1, 11, 31)

// site.js lastDayOfMonth(m, y): not the last day. It's today's day of the month, capped at the
// month's length, which is what makes the overlay "month to date". Format "m-d-yyyy".
function lastDayOfMonth(m, y) {
  let today = nowToday
  const monthDayMax = new Date(y, m, 0).getDate()
  if (today > monthDayMax) today = monthDayMax
  return m + '-' + today + '-' + y
}

function monthRules(field, year) {
  const rules = []
  for (let m = 1; m <= 12; m++) {
    rules.push({
      condition: 'and',
      rules: [
        { id: field, operator: '>=', type: 'date', input: 'select', value: m + '-' + '1' + '-' + year },
        { id: field, operator: '<=', type: 'date', input: 'select', value: lastDayOfMonth(m, year) }
      ]
    })
  }
  return { rules, condition: 'or' }
}

function yearFields(field, start, end) {
  return [
    { field, type: 'Date', oper: '>=', val: start },
    { field, type: 'Date', oper: '<=', val: end }
  ]
}

// insightQuerySeries1..4. `f` holds the filter lists from insightQueryChanged (branch, channel,
// lo, bda, lien, state, division). Series 4 keeps CGS's this-year queryFields: the MTD endpoint
// filters on filterRule only, so the bounds don't change the result.
export function chartSeriesQueries(field, f) {
  const lists = () => ({ channel: f.channel, branch: f.branch, state: f.state, lo: f.lo, bda: f.bda, lien: f.lien, division: f.division })
  return [
    { action: 'GetInsightsData', body: { ...lists(), queryFields: yearFields(field, thisYearStart, thisYearEnd), groupByDate: field, azureId: azureId() } },
    { action: 'GetInsightsData', body: { ...lists(), queryFields: yearFields(field, lastYearStart, lastYearEnd), groupByDate: field, azureId: azureId() } },
    { action: 'GetInsightsChartMTDOverlayData', body: { ...lists(), queryFields: yearFields(field, thisYearStart, thisYearEnd), filterRule: monthRules(field, currentYear), groupByDate: field, azureId: azureId() } },
    { action: 'GetInsightsChartMTDOverlayData', body: { ...lists(), queryFields: yearFields(field, thisYearStart, thisYearEnd), filterRule: monthRules(field, lastYear), groupByDate: field, azureId: azureId() } }
  ]
}

// insightQueryChanged(data) in the chart: no amortization, as CGS.
export function chartFiltersFromEvent(e) {
  return {
    branch: e.branch.slice(),
    channel: e.channel.slice(),
    lo: e.lo.map(lo => lo.email),
    bda: e.bda.map(lo => lo.encompassId),
    lien: e.lien.map(lien => lien.value),
    state: e.state.slice(),
    division: e.division.map(item => item.value)
  }
}

export function emptyChartFilters() {
  return { branch: [], channel: [], lo: [], bda: [], lien: [], state: [], division: [] }
}
